import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, Circle, LogOut } from "lucide-react";
import { MENU_GROUPS, ICON_MAPPING } from "../../constants/menuConstants";
import { Avatar } from "../../ui/Avatar";
import { cx } from "../../ui/format";

const groupItems = (group) =>
  group.subGroups ? group.subGroups.flatMap((sg) => sg.items) : group.items || [];

export const findGroupForPath = (path) => {
  for (const group of MENU_GROUPS) {
    if (groupItems(group).some((item) => item.to === path || path.startsWith(`${item.to}/`))) return group.id;
  }
  return "dashboard";
};

/**
 * The navigation list shared by the desktop sidebar and the mobile drawer.
 * Groups open/close independently; the group holding the current page starts open.
 * `collapsed` shows icons only (desktop).
 */
export const NavMenu = ({ role, loading, collapsed = false, onNavigate }) => {
  const location = useLocation();
  const [open, setOpen] = useState(() => new Set([findGroupForPath(location.pathname)]));

  useEffect(() => {
    const g = findGroupForPath(location.pathname);
    setOpen((prev) => (prev.has(g) ? prev : new Set([...prev, g])));
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="flex flex-col gap-2 px-3 py-2" aria-hidden="true">
        {Array.from({ length: 7 }).map((_, i) => (
          <div key={i} className="h-8 rounded-cm bg-cm-surface-2 animate-pulse" />
        ))}
      </div>
    );
  }

  const isActive = (to) => location.pathname === to || location.pathname.startsWith(`${to}/`);

  const ItemLink = ({ item }) => {
    const Icon = ICON_MAPPING[item.label] || Circle;
    const active = isActive(item.to);
    return (
      <Link
        to={item.to}
        onClick={onNavigate}
        title={collapsed ? item.label : undefined}
        aria-current={active ? "page" : undefined}
        className={cx(
          "group relative flex items-center gap-3 rounded-cm text-[13.5px] font-medium no-underline transition-colors",
          collapsed ? "justify-center h-10 w-10 mx-auto" : "h-9 px-3",
          active
            ? "bg-cm-gold-soft text-cm-text"
            : "text-cm-muted hover:bg-cm-surface-2 hover:text-cm-text",
        )}
      >
        {active && (
          <span
            aria-hidden="true"
            className={cx("absolute top-2 bottom-2 w-[3px] rounded-r bg-cm-red", collapsed ? "-left-2" : "-left-3")}
          />
        )}
        <Icon size={17} className={cx("shrink-0", active ? "text-cm-gold-text" : "")} aria-hidden="true" />
        {!collapsed && <span className="truncate">{item.label}</span>}
      </Link>
    );
  };

  return (
    <nav aria-label="Main" className={cx("flex flex-col", collapsed ? "gap-1 px-2" : "gap-0.5 px-3")}>
      {MENU_GROUPS.map((group) => {
        if (group.hideFromNavigation) return null;
        const visible = groupItems(group).filter((item) => item.visibleCheck(role || {}));
        if (!visible.length) return null;

        if (collapsed) {
          return (
            <div key={group.id} className="flex flex-col gap-1 py-1 border-b border-cm-border last:border-b-0">
              {visible.map((item) => <ItemLink key={item.to} item={item} />)}
            </div>
          );
        }

        const expanded = open.has(group.id);
        const GroupIcon = group.icon;
        const hasActive = visible.some((item) => isActive(item.to));
        return (
          <div key={group.id} className="py-0.5">
            <button
              type="button"
              onClick={() =>
                setOpen((prev) => {
                  const next = new Set(prev);
                  if (next.has(group.id)) next.delete(group.id);
                  else next.add(group.id);
                  return next;
                })
              }
              aria-expanded={expanded}
              className={cx(
                "w-full flex items-center justify-between gap-2 h-8 px-3 rounded-cm text-[11px] font-semibold uppercase tracking-[0.1em] transition-colors",
                hasActive ? "text-cm-gold-text" : "text-cm-faint hover:text-cm-muted",
              )}
            >
              <span className="flex items-center gap-2 min-w-0">
                {GroupIcon && <GroupIcon size={13} aria-hidden="true" />}
                <span className="truncate">{group.title}</span>
              </span>
              <ChevronDown size={14} aria-hidden="true" className={cx("transition-transform", expanded ? "" : "-rotate-90")} />
            </button>
            {expanded && (
              <div className="flex flex-col gap-0.5 mt-0.5 mb-1">
                {visible.map((item) => <ItemLink key={item.to} item={item} />)}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};

/** Signed-in user row. The whole row logs out (kept from the old sidebar). */
export const UserLogoutButton = ({ name, roleName, onLogout, collapsed = false }) => (
  <button
    type="button"
    onClick={onLogout}
    title={collapsed ? `Log out ${name || ""}` : "Log out"}
    className={cx(
      "group w-full flex items-center gap-3 rounded-cm text-left transition-colors hover:bg-cm-surface-2",
      collapsed ? "justify-center p-2" : "p-2",
    )}
  >
    <Avatar name={name} size={34} tone="red" />
    {!collapsed && (
      <>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-cm-text truncate">{name}</span>
          <span className="block text-xs text-cm-muted truncate">{roleName ? `Role: ${roleName}` : "Staff"}</span>
        </span>
        <LogOut size={16} className="text-cm-muted group-hover:text-cm-danger shrink-0" aria-hidden="true" />
      </>
    )}
  </button>
);
